import { useState } from "react";
import { describeChange, type PosMode } from "@tovi/pos-sync";
import { Badge, Button, Modal, useTranslate } from "@tovi/ui";
import { useTill } from "@/lib/use-till.ts";

/**
 * Going offline, and coming back (`08-pos-terminal-and-qr.md` § Offline mode).
 *
 * "Switching modes in either direction requires explicit confirmation." The
 * source is right to insist: offline is not a degraded version of the same
 * till, it is a till that **stops receiving other terminals' orders** — and a
 * cashier who was not told will find out when a table asks where their food
 * is.
 *
 * So the confirmation lists what stops working, in the cashier's own language,
 * and the badge stays on screen for as long as the mode does.
 */
export function ModeSwitch() {
  const t = useTranslate();
  const { till, state } = useTill();
  const [asking, setAsking] = useState<PosMode | null>(null);

  const change = asking ? describeChange(till.mode, asking, queuedOf(state)) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAsking(till.mode === "online" ? "offline" : "online")}
        className="flex items-center gap-2 rounded-control border border-line px-3 py-1"
      >
        <Badge tone={till.mode === "online" ? "success" : "warning"}>
          {t(till.mode === "online" ? "pos.mode.online" : "pos.mode.offline")}
        </Badge>
        {state.kind === "syncing" ? (
          <span className="text-xs text-ink-muted">{t("pos.sync.syncing")}</span>
        ) : state.kind === "waiting" ? (
          <span className="text-xs text-ink-muted">
            {t("pos.sync.waiting", { count: state.remaining })}
          </span>
        ) : state.kind === "needsAttention" ? (
          <span className="text-xs text-danger">
            {t("pos.sync.needsAttention", { count: state.failures })}
          </span>
        ) : null}
      </button>

      {change ? (
        <Modal
          open
          title={t(change.to === "offline" ? "pos.mode.goOffline" : "pos.mode.comeOnline")}
          onClose={() => setAsking(null)}
        >
          <div className="flex flex-col gap-4">
            {change.to === "offline" ? (
              <>
                <p className="text-sm text-ink-body">{t("pos.mode.stillAble")}</p>
                <ul className="list-disc ps-5 text-sm text-ink-muted">
                  <li>{t("pos.offline.ordering")}</li>
                  <li>{t("pos.offline.payment")}</li>
                  <li>{t("pos.offline.printing")}</li>
                </ul>
                <p className="text-sm text-ink-body">{t("pos.mode.butNot")}</p>
                <ul className="list-disc ps-5 text-sm text-danger">
                  {change.warnings.map((warning) => (
                    <li key={warning}>{t(warning as "pos.offline.noIncomingOrders")}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-ink-body">
                {change.queued > 0 ? t("pos.offline.willSync") : t("pos.mode.allSent")}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setAsking(null)}>
                {t(till.mode === "online" ? "pos.mode.stayOnline" : "pos.mode.stayOffline")}
              </Button>
              <Button
                onClick={() => {
                  till.setMode(change.to);
                  setAsking(null);
                }}
              >
                {t(change.to === "online" ? "pos.mode.switchToOnline" : "pos.mode.switchToOffline")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function queuedOf(state: { kind: string; remaining?: number }): number {
  return state.remaining ?? 0;
}
